import { defineStore } from 'pinia';
import CompanyAPI from 'dashboard/api/companies';
import { throwErrorMessage } from 'dashboard/store/utils/api';
import camelcaseKeys from 'camelcase-keys';

export const useCompaniesStore = defineStore('companies', {
  state: () => ({
    records: [],
    selectedCompany: null,
    uiFlags: {
      fetchingList: false,
      fetchingItem: false,
      creating: false,
      updating: false,
      deleting: false,
    },
    meta: {},
  }),

  getters: {
    getCompaniesList: state => camelcaseKeys(state.records, { deep: true }),
    getSelectedCompany: state => {
      return state.selectedCompany
        ? camelcaseKeys(state.selectedCompany, { deep: true })
        : null;
    },
    getUIFlags: state => state.uiFlags,
    getMeta: state => state.meta,
  },

  actions: {
    setUIFlag(flags) {
      this.uiFlags = { ...this.uiFlags, ...flags };
    },

    setMeta(meta) {
      this.meta = meta;
    },

    async search({ search, page, sort }) {
      this.setUIFlag({ fetchingList: true });
      try {
        const {
          data: { payload, meta },
        } = await CompanyAPI.search(search, page, sort);
        this.records = payload;
        this.setMeta(meta);
      } catch (error) {
        throwErrorMessage(error);
      } finally {
        this.setUIFlag({ fetchingList: false });
      }
    },

    async get({ page, sort }) {
      this.setUIFlag({ fetchingList: true });
      try {
        const {
          data: { payload, meta },
        } = await CompanyAPI.get({ page, sort });
        this.records = payload;
        this.setMeta(meta);
      } catch (error) {
        throwErrorMessage(error);
      } finally {
        this.setUIFlag({ fetchingList: false });
      }
    },

    async show(id) {
      this.setUIFlag({ fetchingItem: true });
      try {
        const { data } = await CompanyAPI.show(id);
        this.selectedCompany = data.payload || data;
        return data.payload || data;
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.uiFlags.fetchingItem = false;
      }
    },

    async create(companyData) {
      this.setUIFlag({ creating: true });
      try {
        const { data } = await CompanyAPI.create(companyData);
        const company = data.payload || data;
        this.records.unshift(company);
        return company;
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.setUIFlag({ creating: false });
      }
    },

    async update(id, companyData) {
      this.setUIFlag({ updating: true });
      try {
        let payload = companyData;
        // Check if there's a file (avatar) that needs FormData
        if (companyData.avatar instanceof File) {
          const formData = new FormData();
          Object.keys(companyData).forEach(key => {
            if (companyData[key] !== undefined && companyData[key] !== null) {
              // Rails expects nested params like company[name], company[avatar]
              const formKey = `company[${key}]`;
              if (key === 'metadata' && typeof companyData[key] === 'object') {
                formData.append(formKey, JSON.stringify(companyData[key]));
              } else {
                formData.append(formKey, companyData[key]);
              }
            }
          });
          payload = formData;
        }

        const { data } = await CompanyAPI.update(id, payload);
        const company = data.payload || data;
        const index = this.records.findIndex(c => c.id === id);
        if (index !== -1) {
          this.records[index] = company;
        }
        if (this.selectedCompany?.id === id) {
          this.selectedCompany = company;
        }
        return company;
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.setUIFlag({ updating: false });
      }
    },

    async delete(id) {
      this.setUIFlag({ deleting: true });
      try {
        await CompanyAPI.delete(id);
        this.records = this.records.filter(c => c.id !== id);
        if (this.selectedCompany?.id === id) {
          this.selectedCompany = null;
        }
        return true;
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.setUIFlag({ deleting: false });
      }
    },

    clearSelectedCompany() {
      this.selectedCompany = null;
    },
  },
});
